import { useEffect, useMemo, useState } from "react";
import { RefreshCcw, Copy, Check } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import {
  addWarehouseAndFulfillAmazonOrder,
  getStoredAmazonOrders,
} from "../api/amazonOrdersApi";



const hasValue = (value) => {
  if (value == null) {
    return false;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "n/a" && normalized !== "null";
};

const getStatusTone = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("shipped")) {
    return "bg-black text-white";
  }
  if (normalized.includes("cancel")) {
    return "bg-gray-300 text-black";
  }
  if (normalized.includes("pending") || normalized.includes("unshipped")) {
    return "bg-gray-700 text-white";
  }

  return "bg-gray-200 text-black";
};

const getQueueTime = (order) => {
  const value =
    order?.queuedAt || order?.processingStartedAt || order?.processedAt;
  const timestamp = value ? new Date(value).getTime() : 0;

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getPurchaseTime = (order) => {
  const timestamp = order?.purchaseDate
    ? new Date(order.purchaseDate).getTime()
    : NaN;

  return Number.isFinite(timestamp) ? timestamp : null;
};

const buildOrderItemsPayload = (order) => {
  if (Array.isArray(order.orderItems) && order.orderItems.length) {
    return order.orderItems.map((item) => ({
      uom: "un.",
      qty: Number(
        item?.QuantityOrdered || item?.quantityOrdered || item?.qty || 1,
      ),
      price: Number(
        item?.ItemPrice?.Amount ||
          item?.itemPrice?.amount ||
          item?.price ||
          order.orderTotal ||
          0,
      ),
      sellerSkus:
        item?.SellerSKU ||
        item?.sellerSku ||
        item?.sellerSkus ||
        order.sellerSkus,
    }));
  }

  return [
    {
      uom: "un.",
      qty: 1,
      price: Number(order.orderTotal || 0),
      sellerSkus: order.sellerSkus,
    },
  ];
};

const isAutomationCompleted = (automationStatus) => {
  const normalized = String(automationStatus || "").toLowerCase();

  return (
    normalized.includes("completed") ||
    normalized.includes("complete") ||
    normalized.includes("fulfilled") ||
    normalized.includes("success") ||
    normalized.includes("done")
  );
};

const shouldReuseSalesOrder = (order) => {
  if (typeof order?.useExistingSalesOrder === "boolean") {
    return order.useExistingSalesOrder;
  }

  const automationAction = String(order?.automationAction || "").toLowerCase();

  if (
    automationAction.includes("existing") ||
    automationAction.includes("reuse") ||
    automationAction.includes("use_so")
  ) {
    return true;
  }

  if (
    automationAction.includes("create") ||
    automationAction.includes("new sales") ||
    automationAction.includes("new_so")
  ) {
    return false;
  }

  return hasValue(order?.salesOrderNumber);
};

const getAutomationRouteLabel = (order) => {
  if (shouldReuseSalesOrder(order)) {
    return `Use SO ${order.salesOrderNumber}`;
  }

  return "Create new SO";
};

export default function AmazonOrders() {
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("all");
  const [copiedField, setCopiedField] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
const ORDERS_PER_PAGE = 25;
  const loadOrders = async ({ showLoader = true } = {}) => {
    if (showLoader) {
      setLoading(true);
    }
    setError("");

    try {
      const response = await getStoredAmazonOrders();
      setOrders(response.orders || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load Amazon orders.",
      );
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };
 {/* REPLACE handleResetFilters */}

  const handleResetFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
    setStatusFilter("all");
    setMarketplaceFilter("all");
  };
  const handleToggleOrderSelection = (orderId, automationStatus) => {
    if (isAutomationCompleted(automationStatus)) {
      return;
    }

    setSelectedOrderIds((currentSelectedOrderIds) => {
      const isSelected = currentSelectedOrderIds.includes(orderId);
      const nextSelectedOrderIds = isSelected
        ? currentSelectedOrderIds.filter(
            (selectedOrderId) => selectedOrderId !== orderId,
          )
        : [...currentSelectedOrderIds, orderId];

      setActionMessage(
        nextSelectedOrderIds.length
          ? `${nextSelectedOrderIds.length} order(s) selected for automation.`
          : "No order IDs selected.",
      );

      return nextSelectedOrderIds;
    });
  };
  const handleCopy = async (value, key) => {
    if (!value || value === "N/A") return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(key);

      setTimeout(() => {
        setCopiedField("");
      }, 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };
  const handleProcessSelectedOrders = async () => {
    if (!selectedOrderIds.length) {
      const message = "Select at least one order ID first.";
      setActionMessage(message);
      toast.error(message);
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const selectedOrders = orders.filter((order) =>
        selectedOrderIds.includes(order.amazonOrderId),
      );

      for (const order of selectedOrders) {
        const payload = {
          amazonOrderId: order.amazonOrderId,
          marketplaceId: order.marketplaceId,
          headless: false,
          priority: "highest",
          requestedFrom: "ui",
          tag: order.amazonOrderId,
          salesOrderNumber: hasValue(order.salesOrderNumber)
            ? order.salesOrderNumber
            : null,
          salesOrderId: hasValue(order.salesOrderId)
            ? order.salesOrderId
            : null,
          useExistingSalesOrder: shouldReuseSalesOrder(order),
          items: buildOrderItemsPayload(order),
        };

        const result = await addWarehouseAndFulfillAmazonOrder(payload);

        if (result?.success === false) {
          throw new Error(
            result?.details ||
              result?.error ||
              "Failed to process selected order.",
          );
        }
      }

      const successMessage = `${selectedOrders.length} selected order(s) set to highest priority. You can now select and submit more orders.`;
      setActionMessage(successMessage);
      toast.success(successMessage, { duration: 5000 });
      setSelectedOrderIds([]);
      void loadOrders({ showLoader: false });
    } catch (err) {
      const errorMessage =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to process selected orders.";

      setError(errorMessage);
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const stats = useMemo(() => {
    const queuedOrders = orders.filter(
      (order) => !isAutomationCompleted(order.automationStatus),
    ).length;
    const reuseSalesOrderOrders = orders.filter(shouldReuseSalesOrder).length;

    return {
      queuedOrders,
      reuseSalesOrderOrders,
    };
  }, [orders]);



{/* REPLACE filteredOrders useMemo */}

  const filteredOrders = useMemo(() => {
      setCurrentPage(1); 
    const query = search.trim().toLowerCase();

    const startTime = fromDate
      ? new Date(`${fromDate}T00:00:00`).getTime()
      : null;

    const endTime = toDate
      ? new Date(`${toDate}T23:59:59.999`).getTime()
      : null;

    return orders
      .filter((order) => {
        const purchaseTime = getPurchaseTime(order);

        const marketplaceName =
          order.marketplaceId === "ATVPDKIKX0DER"
            ? "Amazon.com"
            : order.marketplaceId === "A2EUQ1WTGCTBG2"
              ? "Amazon.ca"
              : order.marketplaceId;

        const matchesSearch =
          query === "" ||
          String(order.amazonOrderId).toLowerCase().includes(query) ||
          String(order.salesOrderNumber).toLowerCase().includes(query) ||
          String(order.sellerSkus).toLowerCase().includes(query);

     {/* REPLACE matchesStatus inside filteredOrders */}

const currentStatus = String(order.automationStatus || "").toLowerCase();

        const matchesStatus =
          statusFilter === "all" ||

          (statusFilter === "Queued" &&
            (currentStatus.includes("queue") ||
              currentStatus.includes("pending"))) ||

          (statusFilter === "Processing" &&
            currentStatus.includes("processing")) ||

          (statusFilter === "Completed" &&
            (currentStatus.includes("completed") ||
              currentStatus.includes("success") ||
              currentStatus.includes("done") ||
              currentStatus.includes("fulfilled"))) ||

          (statusFilter === "Failed" &&
            (currentStatus.includes("fail") ||
              currentStatus.includes("error")));

        const matchesMarketplace =
          marketplaceFilter === "all" || marketplaceName === marketplaceFilter;

        const matchesFromDate =
          startTime == null ||
          (purchaseTime != null && purchaseTime >= startTime);

        const matchesToDate =
          endTime == null || (purchaseTime != null && purchaseTime <= endTime);

        return (
          matchesSearch &&
          matchesStatus &&
          matchesMarketplace &&
          matchesFromDate &&
          matchesToDate
        );
      })
      // .sort((a, b) => getQueueTime(b) - getQueueTime(a));
      .sort((a, b) => {
  const getPriority = (status) => {
    const s = String(status || "").toLowerCase();

    if (s.includes("processing")) return 1;
    if (s.includes("queue")) return 2;
    if (s.includes("pending")) return 3;
    if (
      s.includes("completed") ||
      s.includes("failed") 
    )
      return 4;

    return 99;
  };

  const priorityDiff =
    getPriority(a.automationStatus) - getPriority(b.automationStatus);

  if (priorityDiff !== 0) return priorityDiff;

  // fallback: latest first
  return getQueueTime(b) - getQueueTime(a);
});
  }, [orders, search, statusFilter, marketplaceFilter, fromDate, toDate]);
  {
    /* ADD THIS useMemo BELOW stats */
  }
   const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));

    const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  const availableStatuses = ["Queued", "Processing", "Completed", "Failed"];
  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-3xl border-gray-200 bg-gray-50 p-6 text-black shadow-lg">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-400">
                Amazon
              </p>
              <h1 className="mt-2 text-3xl font-bold">Stored Orders</h1>
              {/* <p className="mt-2 max-w-2xl text-sm text-gray-300">
                Review every Amazon order saved in the database, then inspect
                marketplace, status, and raw order details in one place.
              </p> */}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={loadOrders}
                disabled={loading || processing}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-black px-4 py-3 text-sm font-semibold text-black hover:bg-gray-200 hover:text-black disabled:cursor-not-allowed disabled:opacity-70"
              >
                <RefreshCcw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                Fetch Orders
              </button>
              <button
                type="button"
                onClick={handleProcessSelectedOrders}
                disabled={!selectedOrderIds.length || loading || processing}
                className="rounded-2xl bg-black border border-black px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {processing ? "Processing..." : "Process Selected Orders"}
              </button>
              {/* <button
                type="button"
                className="rounded-2xl border border-black px-4 py-3 text-sm font-semibold text-black/70 disabled:cursor-not-allowed"
                disabled
                title="Automation endpoint not connected yet"
              >
                Fix Existing Orders
              </button> */}
            </div>
          </div>
        </section>

        <section className="rounded-3xl bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid grid-cols-2 gap-3 lg:w-auto xl:grid-cols-3">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  Total
                </p>
                <p className="mt-2 text-2xl font-bold text-black">
                  {orders.length}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  On Filter
                </p>
                <p className="mt-2 text-2xl font-bold text-black">
                  {filteredOrders.length}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  In Queue
                </p>
                <p className="mt-2 text-2xl font-bold text-black">
                  {stats.queuedOrders}
                </p>
              </div>
              {/* <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  Reuse SO
                </p>
                <p className="mt-2 text-2xl font-bold text-black">
                  {stats.reuseSalesOrderOrders}
                </p>
              </div> */}
            </div>

            {/* REPLACE FILTER UI SECTION */}

            {/* REPLACE FILTER SECTION WITH THIS */}

            <div className="flex w-full flex-wrap gap-3 items-end">
              {/* Status */}
              <div className="flex flex-col min-w-[170px]">
                <label className="mb-1 text-sm font-medium text-gray-600">
                  Status
                </label>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none"
                >
                  <option value="all">All Status</option>
                  <option value="Queued">Queued</option>
                  <option value="Processing">Processing</option>
                  <option value="Completed">Completed</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>

              {/* Marketplace */}
              <div className="flex flex-col min-w-[170px]">
                <label className="mb-1 text-sm font-medium text-gray-600">
                  Marketplace
                </label>

                <select
                  value={marketplaceFilter}
                  onChange={(e) => setMarketplaceFilter(e.target.value)}
                  className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none"
                >
                  <option value="all">All Marketplace</option>
                  <option value="Amazon.com">Amazon.com</option>
                  <option value="Amazon.ca">Amazon.ca</option>
                </select>
              </div>

              {/* From Date */}
              <div className="flex flex-col min-w-[170px]">
                <label className="mb-1 text-sm font-medium text-gray-600">
                  From Date
                </label>

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none"
                />
              </div>

              {/* To Date + Clear Button Below */}
              <div className="flex flex-col min-w-[170px] gap-3">
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-gray-600">
                    To Date
                  </label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleResetFilters}
                className="rounded-2xl bg-black px-4 py-3 text-sm font-semibold text-white"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {actionMessage ? (
            <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {actionMessage}
            </div>
          ) : null}

          {!loading && !error && (
            <>
            
            <div className="overflow-hidden rounded-2xl border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-black text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Action
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Marketplace
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Order ID
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Order Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Currency
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        Automation Status
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                        ERPAG SO
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    
                   {paginatedOrders.length===0?(
                    <tr>
                      <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-500 text-md"
                      >
                        No data found 
                      
                      </td>
                    </tr>
                   ):(
                    <>
                     {paginatedOrders.map((order, index) => ( 
                      <tr
                        key={order.id || index}
                        className={`hover:bg-gray-50 ${
                          selectedOrderIds.includes(order.amazonOrderId)
                            ? "bg-gray-100"
                            : ""
                        }`}
                      >
                        {/* Action Column */}
                        <td className="px-4 py-4 text-sm text-black">
                          {isAutomationCompleted(order.automationStatus) ? (
                            <span className="text-xs font-semibold text-gray-400">
                              Done
                            </span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.includes(
                                order.amazonOrderId,
                              )}
                              onChange={() =>
                                handleToggleOrderSelection(
                                  order.amazonOrderId,
                                  order.automationStatus,
                                )
                              }
                              className="h-4 w-4 cursor-pointer rounded border-gray-300"
                            />
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.purchaseDate
                            ? new Date(order.purchaseDate).toLocaleDateString()
                            : "N/A"}
                        </td>

                        {/* Marketplace */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.marketplaceId === "ATVPDKIKX0DER"
                            ? "Amazon.com"
                            : order.marketplaceId === "A2EUQ1WTGCTBG2"
                              ? "Amazon.ca"
                              : order.marketplaceId}
                        </td>

                        {/* Order ID */}
                        <td className="px-4 py-4 text-sm font-medium text-black">
                          <div className="flex items-center gap-2">
                            <span>{order.amazonOrderId}</span>

                            <button
                              type="button"
                              onClick={() =>
                                handleCopy(
                                  order.amazonOrderId,
                                  `order-${order.amazonOrderId}`,
                                )
                              }
                              className="text-gray-500 hover:text-black"
                            >
                              {copiedField ===
                              `order-${order.amazonOrderId}` ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.orderTotal ?? "N/A"}
                        </td>

                        {/* Currency */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.currencyCode || "N/A"}
                        </td>

                        {/* Automation */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.automationStatus}
                        </td>

                        {/* ERPAG SO */}
                        <td className="px-4 py-4 text-sm text-black">
                          {order.salesOrderNumber ? (
                            <div className="flex items-center gap-2">
                              <span>{order.salesOrderNumber}</span>

                              <button
                                type="button"
                                onClick={() =>
                                  handleCopy(
                                    order.salesOrderNumber,
                                    `so-${order.amazonOrderId}`,
                                  )
                                }
                                className="text-gray-500 hover:text-black"
                              >
                                {copiedField === `so-${order.amazonOrderId}` ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                    </>
                   )
                  }
                  </tbody>
                </table>
              </div>
            </div>
             {filteredOrders.length > 0 && (
                <div className="mt-4 flex items-center justify-between px-1">
                  <span className="text-sm text-gray-500">
                    {selectedOrderIds.length} selected
                  </span>
 
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="rounded-2xl border border-gray-300 px-4 py-2 text-sm font-medium text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Previous
                    </button>
 
                    <span className="text-sm font-semibold text-black">
                      Page {currentPage} of {totalPages}
                    </span>
 
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="rounded-2xl border border-gray-300 px-4 py-2 text-sm font-medium text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
